// BankaTab — Nastavenia → 🏦 Banka (Mirko 2026-07-04)
// Fronta „Import na odsúhlasenie" pre bankové výpisy VÚB (camt.053 z Gmail).
// Ústava 12.10 (nič nemení FA bez potvrdenia Office) · 13.4 (UPSERT dedup) · 14.4/17.2 (párovanie VS+suma).
// Saldo F1a (15. 9. 2026, Mirko: „nemôže byť iba potvrď, musí byť priradiť“): Priradiť otvorí rozdelenie platby na doklady v salde (PriraditPlatbu);
// hromadné potvrdenie návrhov je vypnuté — úhrady sa priraďujú ručne (Kronika 479 bod 1).
import React, { useState, useEffect, useMemo } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, SK, hladaj } from '../theme.js'
import { KpiCard, KpiRow, SearchInput, FilterChips } from '../components/Ui.jsx'
import { Btn, BtnGhost } from '../components/Btn.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import PriraditPlatbu from '../components/PriraditPlatbu.jsx'

const STATUS_CHIPS = [
  { key: 'proposed',  label: 'Návrhy' },
  { key: 'pending',   label: 'Nespárované' },
  { key: 'confirmed', label: 'Potvrdené' },
  { key: 'info',      label: 'Poplatky/interné' },
  { key: 'rejected',  label: '✕ Zamietnuté' },
  { key: '',          label: 'Všetko' },
]

const STATUS_BADGE = {
  proposed:  { label: 'návrh',       color: '#15803d', bg: '#dcfce7' },
  pending:   { label: 'nespárované', color: '#92400e', bg: '#fef3c7' },
  confirmed: { label: 'potvrdené',   color: '#1d4ed8', bg: '#dbeafe' },
  rejected:  { label: 'zamietnuté',  color: '#b91c1c', bg: '#fef2f2' },
  info:      { label: 'info',        color: '#64748b', bg: '#f1f5f9' },
}

export default function BankaTab() {
  const toast = useToast()
  const [summary, setSummary] = useState(null)
  const [filter, setFilter] = useState('proposed')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [priradit, setPriradit] = useState(null)   // id platby v okne Priradiť

  const reload = async () => {
    setLoading(true)
    try {
      const [s, l] = await Promise.all([
        fetchAuth('/api/bank/summary').then(r => r.ok ? r.json() : {}),
        // fa_banka_v1: v databáze je 17 175 nespárovaných, obrazovka ich sťahovala 300 a hľadala
        // len v nich — platba spred troch týždňov sa nedala nájsť. Server hľadanie aj stránkovanie
        // vie, len mu to nikto neposielal.
        fetchAuth('/api/bank/transactions?limit=1000'
          + (filter ? `&status=${filter}` : '')
          + (search.trim() ? `&q=${encodeURIComponent(search.trim())}` : '')
        ).then(r => r.ok ? r.json() : []),
      ])
      setSummary(s)
      setRows(Array.isArray(l) ? l : [])
    } catch (e) { toast.error('Chyba načítania: ' + e.message) }
    finally { setLoading(false) }
  }
  // fa_banka_v1: hľadanie ide na server, ale až keď človek dopíše (400 ms), nie po každom písmene
  useEffect(() => { const t = setTimeout(reload, search.trim() ? 400 : 0); return () => clearTimeout(t) }, [filter, search])

  // fa_banka_v1: hľadá server nad celou databázou; tu sa už len dofiltruje, čo prišlo
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r =>
      hladaj((r.cp_name || ''), q) ||
      hladaj((r.description || ''), q) ||
      hladaj((r.vs || ''), q) ||
      hladaj((r.match_note || ''), q)
    )
  }, [rows, search])

  const niceDate = (s) => {
    if (!s) return '—'
    const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})/)
    return m ? `${m[3]}.${m[2]}.${m[1]}` : s
  }

  const doImportNow = async () => {
    setBusy(true)
    try {
      const r = await fetchAuth('/api/bank/import-now', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      const d = await r.json()
      if (!r.ok || d.error) throw new Error(d.error || ('HTTP ' + r.status))
      toast.success(`✓ Import: ${d.scanned} mailov, ${d.txNew} nových pohybov, ${d.proposed} návrhov`)
      reload()
    } catch (e) { toast.error('Import zlyhal: ' + e.message) }
    finally { setBusy(false) }
  }

  const doReject = async (tx) => {
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/bank/tx/${tx.id}/reject`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: 'zamietnuté ručne' }) })
      const d = await r.json()
      if (!r.ok || d.error) throw new Error(d.error || ('HTTP ' + r.status))
      toast.success('✓ Zamietnuté')
      reload()
    } catch (e) { toast.error('Chyba: ' + e.message) }
    finally { setBusy(false) }
  }

  const by = summary?.byStatus || {}

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>

      {/* ── KPI súhrn ── */}
      <KpiRow cols={5}>
        <KpiCard title="Návrhy na potvrdenie" value={by.proposed?.count || 0} color={(by.proposed?.count || 0) > 0 ? COLORS.add : COLORS.muted} />
        <KpiCard title="Nespárované" value={by.pending?.count || 0} color={(by.pending?.count || 0) > 0 ? '#f59e0b' : COLORS.muted} />
        <KpiCard title="Potvrdené" value={by.confirmed?.count || 0} color={COLORS.nav} />
        <KpiCard title="Posledný import" value={summary?.lastRun ? niceDate(summary.lastRun.finished_at || summary.lastRun.started_at) : '—'} color={COLORS.muted} />
        <KpiCard title="Pohybov celkom" value={Object.values(by).reduce((a, s) => a + (s.count || 0), 0)} color={COLORS.muted} />
      </KpiRow>

      {/* ── Akcie ── */}
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn onClick={doImportNow} disabled={busy}>▶ Spustiť import teraz</Btn>
        <BtnGhost onClick={reload} disabled={busy}>Obnoviť</BtnGhost>
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Úhrady sa priraďujú ručne tlačidlom Priradiť pri platbe: rozdelenie na faktúry, zádržné, zápočet alebo preplatok.</span>
      </div>

      {/* ── Filter ── */}
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterChips options={STATUS_CHIPS} value={filter} onChange={setFilter} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Hľadať (protistrana / VS / popis)…" />
        </div>
      </div>

      {/* ── Tabuľka pohybov ── */}
      <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
          <thead>
            <tr style={{ background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}` }}>
              {['Dátum', 'Smer', 'Suma', 'Protistrana', 'VS', 'Popis', 'Návrh párovania', 'Stav', 'Akcia'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left',
                  fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
                  color: COLORS.muted, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Načítavam…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>
                {search ? 'Žiadne výsledky vyhľadávania.' : 'Žiadne pohyby v tejto kategórii.'}
              </td></tr>
            ) : filtered.map((r, i) => {
              const badge = STATUS_BADGE[r.match_status] || { label: r.match_status, color: COLORS.muted, bg: COLORS.bgSoft }
              const credit = r.cdt_dbt === 'CRDT'
              return (
                <tr key={r.id} style={{
                  borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}`,
                  background: i % 2 ? COLORS.bgSoft : 'transparent',
                }}>
                  <td style={cell}>{niceDate(r.booking_date)}</td>
                  <td style={{ ...cell, fontWeight: 700, color: credit ? '#15803d' : '#b91c1c' }}>{credit ? '↓ prijaté' : '↑ odoslané'}</td>
                  <td style={{ ...cell, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 700, color: credit ? '#15803d' : COLORS.text }}>{SK.eur(r.amount || 0)}</td>
                  <td style={{ ...cell, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cp_name || '—'}</td>
                  <td style={{ ...cell, fontFamily: TYPO.mono }}>{r.vs || '—'}</td>
                  <td style={{ ...cell, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COLORS.muted }} title={r.description || ''}>{r.description || '—'}</td>
                  <td style={{ ...cell, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.match_note || ''}>{r.match_note || '—'}</td>
                  <td style={cell}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: TYPO.sizes.xs, fontWeight: 600,
                    color: badge.color, background: badge.bg }}>{badge.label}</span></td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    {(r.match_status === 'proposed' || r.match_status === 'pending' || r.match_status === 'rejected') && (
                      <button onClick={() => setPriradit(r.id)} disabled={busy} style={btnMini('#15803d', '#dcfce7')} title="Rozdeliť platbu na faktúry, zádržné, zápočet alebo preplatok">Priradiť</button>
                    )}
                    {(r.match_status === 'proposed' || r.match_status === 'pending') && (
                      <button onClick={() => doReject(r)} disabled={busy} style={btnMini('#64748b', '#f1f5f9')}>Nie je FA</button>
                    )}
                    {r.match_status === 'confirmed' && (<>
                      {r.match_type === 'saldo' && <button onClick={() => setPriradit(r.id)} style={btnMini('#1d4ed8', '#dbeafe')}>Rozpis</button>}
                      {r.confirmed_by || ''}
                    </>)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, padding: '4px 8px' }}>
        Zobrazených {filtered.length} z {rows.length} pohybov · Účet VÚB SK28…1151, denný XML export z Gmail · Priradiť zapíše rozdelenie platby do salda v Matrixe; faktúry z MKsoftu Matrix nemení (17.1)
      </div>
      {priradit != null && <PriraditPlatbu txId={priradit} onClose={() => setPriradit(null)} onSaved={() => { setPriradit(null); reload() }} />}
    </div>
  )
}

const cell = { padding: '7px 10px', verticalAlign: 'middle' }
const btnMini = (color, bg) => ({
  border: `1px solid ${color}33`, background: bg, color, borderRadius: 4,
  padding: '3px 8px', marginRight: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
})
